import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ENVIRONMENT_CONFIG_KEYS, ENVIRONMENT_CONFIG_KEY_PARAM, SECURITY_ENC_DECRYPT } from '../constants/api-endpoints';
import { EncryptionService } from './encryption.service';

export interface EncryptedEnvelope {
  v: number;
  iv: string;
  ts: number;
  payload: string;
}

interface EncDecryptResponse {
  data?: { ticket: string; serverPublicKey: string; salt: string; expiresAt: string };
}

const ENVELOPE_VERSION = 1;
const KEY_DERIVATION_INFO = 'tsp-enc-v1';
const IV_BYTES = 12;

@Injectable({ providedIn: 'root' })
export class EncryptionSessionService {
  private readonly http = inject(HttpClient);
  private readonly configs = inject(EncryptionService);

  private enabled = false;
  private ticket?: string;
  private key?: CryptoKey;
  private pending?: Promise<void>;

  get isEnabled(): boolean {
    return this.enabled;
  }

  get isReady(): boolean {
    return this.enabled && !!this.key && !!this.ticket;
  }

  get currentTicket(): string | undefined {
    return this.ticket;
  }

  initialize(): Promise<void> {
    this.pending ??= this.start().finally(() => (this.pending = undefined));
    return this.pending;
  }

  refresh(): Promise<void> {
    this.key = undefined;
    this.ticket = undefined;
    return this.initialize();
  }

  isEnvelope(value: unknown): value is EncryptedEnvelope {
    const candidate = value as Partial<EncryptedEnvelope> | null;
    return (
      !!candidate &&
      typeof candidate === 'object' &&
      candidate.v === ENVELOPE_VERSION &&
      typeof candidate.iv === 'string' &&
      typeof candidate.payload === 'string' &&
      typeof candidate.ts === 'number'
    );
  }

  async encrypt(plainText: string, associatedBase: string): Promise<EncryptedEnvelope> {
    const key = this.requireKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ts = Math.floor(Date.now() / 1000);

    const cipher = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: this.encode(`${associatedBase}|${ts}`) },
      key,
      this.encode(plainText)
    );

    return { v: ENVELOPE_VERSION, iv: this.toBase64(iv), ts, payload: this.toBase64(new Uint8Array(cipher)) };
  }

  async decrypt(envelope: EncryptedEnvelope, associatedBase: string): Promise<string> {
    const key = this.requireKey();

    const plain = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: this.fromBase64(envelope.iv),
        additionalData: this.encode(`${associatedBase}|${envelope.ts}`)
      },
      key,
      this.fromBase64(envelope.payload)
    );

    return new TextDecoder().decode(plain);
  }

  private async start(): Promise<void> {
    this.enabled = await this.readFlag();

    if (!this.enabled) {
      this.key = undefined;
      this.ticket = undefined;
      return;
    }

    if (!this.isReady) {
      await this.handshake();
    }
  }

  private async readFlag(): Promise<boolean> {
    try {
      const configs = await firstValueFrom(
        this.configs.getEnvironmentConfigs({
          [ENVIRONMENT_CONFIG_KEY_PARAM]: ENVIRONMENT_CONFIG_KEYS.ENCRYPTION_ENABLED
        })
      );

      const match = configs.find((config) => config.key === ENVIRONMENT_CONFIG_KEYS.ENCRYPTION_ENABLED);
      return match?.value?.trim().toLowerCase() === 'true';
    } catch {
      return false;
    }
  }

  private async handshake(): Promise<void> {
    try {
      const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
      const clientPublicKey = this.toBase64(new Uint8Array(await crypto.subtle.exportKey('spki', pair.publicKey)));

      const response = await firstValueFrom(
        this.http.post<EncDecryptResponse>(SECURITY_ENC_DECRYPT,{ clientPublicKey })
      );
      const reply = response?.data;
      if (!reply?.ticket || !reply.serverPublicKey || !reply.salt) {
        throw new Error('The handshake reply was incomplete.');
      }

      const serverKey = await crypto.subtle.importKey(
        'spki',
        this.fromBase64(reply.serverPublicKey),
        { name: 'ECDH', namedCurve: 'P-256' },
        false,
        []
      );
      const shared = await crypto.subtle.deriveBits({ name: 'ECDH', public: serverKey }, pair.privateKey, 256);
      const material = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);

      this.key = await crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: this.fromBase64(reply.salt), info: this.encode(KEY_DERIVATION_INFO) },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
      this.ticket = reply.ticket;
    } catch (error) {
      this.key = undefined;
      this.ticket = undefined;
      console.error('[encryption] Could not establish a secure session:', error);
    }
  }

  private requireKey(): CryptoKey {
    if (!this.key) {
      throw new Error('The secure session is not established.');
    }
    return this.key;
  }

  private encode(value: string): Uint8Array {
    return new TextEncoder().encode(value);
  }

  private toBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCodePoint(b)));
    return btoa(binary);
  }

  private fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.codePointAt(i) ?? 0;
    }
    return bytes;
  }
}
